import type { ErrorEvent } from '@sentry/react';

const diagnosticTags = new Set([
  'diagnostic_event',
  'operation',
  'reason',
  'app_version',
  'synthetic',
  'deployment_id',
  'built_at',
  'cache_name',
  'build_branch',
]);

function scrubString(value: string): string {
  return value
    .replace(/\/((?:app\/)?routine-preview)\/[^\s/?#"'<>\\]+/gi, '/$1/[token]')
    .replace(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi, '[email]');
}

function scrubValue(value: unknown): unknown {
  if (typeof value === 'string') return scrubString(value);
  if (Array.isArray(value)) return value.map(scrubValue);
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        scrubString(key),
        scrubValue(entry),
      ]),
    );
  }
  return value;
}

/** Diagnostics cannot inherit routine content, account data, or request payloads. */
export function sanitizeSentryEvent(event: ErrorEvent): ErrorEvent {
  if (event.tags?.diagnostic_event === 'web-operation-failed') {
    return {
      type: event.type,
      event_id: event.event_id,
      timestamp: event.timestamp,
      release: event.release,
      environment: event.environment,
      platform: event.platform,
      level: 'error',
      message: 'Forma web operation failed',
      fingerprint: event.fingerprint,
      tags: Object.fromEntries(
        Object.entries(event.tags).filter(([key]) => diagnosticTags.has(key)),
      ),
    };
  }

  if (event.request?.url)
    event.request.url = event.request.url.split(/[?#]/)[0];
  delete event.request?.query_string;
  delete event.request?.data;
  delete event.request?.cookies;
  delete event.user;
  return scrubValue(event) as ErrorEvent;
}
